import { useEffect, useState, useRef } from "react";
import { createContext } from "react";
import store from "../store";
import { useSyncedStore } from "@syncedstore/react";
import nodeTypes from "../nodes";
import { addToScene } from "../three/toMesh";
import { isArray } from "../utils";

const ComputeContext = createContext();

export function ComputeProvider({ children }) {
  const edges = useSyncedStore(store.edges);
  const nodes = useSyncedStore(store.nodes);
  const controlValues = useSyncedStore(store.controlValues);

  const [sceneLoaded, setSceneLoaded] = useState(false);
  const [computedValues, setComputedValues] = useState({});

  const tempComputedValues = useRef(computedValues);

  useEffect(() => {
    tempComputedValues.current = computedValues;
  }, [computedValues]);

  function getInputs(nodeId, nodeComponent) {
    const inputConnections = edges.filter((edge) =>
      edge.target.startsWith(nodeId)
    );

    return inputConnections.reduce((acc, input) => {
      const [_, property] = input.target.split(":");
      const acceptsList =
        nodeComponent?.inputs?.find((i) => i.id === property).list === true;

      const cachedValue = tempComputedValues.current[input.source];
      // TODO: Handle multiple insputs
      const prevValue = acc[property];

      console.log({ cachedValue, prevValue, acceptsList, input });

      if (cachedValue) {
        if (prevValue && !isArray(prevValue)) {
          const cached = isArray(cachedValue) ? cachedValue : [cachedValue];
          return {
            ...acc,
            [property]: [prevValue, ...cached],
          };
        }
        if (prevValue && isArray(prevValue)) {
          const cached = isArray(cachedValue) ? cachedValue : [cachedValue];
          return {
            ...acc,
            [property]: [...prevValue, ...cached],
          };
        }
        return {
          ...acc,
          [property]: isArray(cachedValue) ? cachedValue : [cachedValue],
        };
      }
      return acc;
    }, {});
  }

  function getControls(nodeId) {
    const node = nodes.find((n) => n.id === nodeId);
    const component = nodeTypes.find((n) => n.name === node.component);

    return component.controls.reduce((acc, control) => {
      const thingies = JSON.parse(JSON.stringify(controlValues));

      if (thingies.hasOwnProperty(`${nodeId}:${control.id}`)) {
        return {
          ...acc,
          [control.id]: thingies[`${nodeId}:${control.id}`],
        };
      }
      return acc;
    }, {});
  }

  function computeOutput({ nodeId, output, inputs, controls, cachedValues }) {
    try {
      const value = output.compute(inputs, controls, nodeId);

      console.log({ output, cachedValues, inputs, controls, value });

      addToScene(`${nodeId}:${output.id}`, value);

      const connections = edges.filter((edge) =>
        edge.source.startsWith(nodeId)
      );

      if (!connections.length) {
        tempComputedValues.current = {
          ...tempComputedValues.current,
          [`${nodeId}:${output.id}`]: value,
        };
        setComputedValues((prevValue) => tempComputedValues.current);
      }

      connections.forEach((connection) => {
        const [nextNodeId] = connection.target.split(":");
        //TODO:  Use a tempRef instead to mutate cachedvalues so previouus
        // computed nodes reference updated values
        tempComputedValues.current = {
          ...tempComputedValues.current,
          [`${nodeId}:${output.id}`]: value,
        };
        computeNode(nextNodeId);
      });
    } catch (e) {
      console.log(e);
    }
  }

  function computeNode(nodeId) {
    const node = nodes.find((node) => node.id === nodeId);
    if (!node) return;
    const nodeComponent = nodeTypes.find((n) => n.name === node.component);

    nodeComponent.outputs.forEach((output) => {
      const inputs = getInputs(nodeId, nodeComponent);
      const controls = getControls(nodeId);

      computeOutput({
        nodeId,
        output,
        inputs,
        controls,
      });
    });
  }

  function setControlValue(id, value) {
    controlValues[id] = value;
  }

  return (
    <ComputeContext.Provider
      value={{
        state: {
          sceneLoaded,
          controlValues,
          computedValues,
        },
        methods: {
          computeNode,
          getControls,
          getInputs,
          setSceneLoaded,
          setControlValue,
        },
      }}
    >
      {sceneLoaded &&
        edges.map((edge) => (
          <EdgeEffect
            key={edge.id}
            edge={edge}
            computedValues={computedValues}
            computeNode={computeNode}
          ></EdgeEffect>
        ))}
      {sceneLoaded &&
        Object.keys(controlValues).map((id) => (
          <ControlValues
            key={id}
            id={id}
            computedValues={computedValues}
            controlValues={controlValues}
            computeNode={computeNode}
          ></ControlValues>
        ))}
      {children}
    </ComputeContext.Provider>
  );
}

function EdgeEffect({ edge, computeNode, computedValues }) {
  useEffect(() => {
    const [nodeId] = edge.source.split(":");
    computeNode(nodeId, computedValues);

    return () => {
      computeNode(nodeId, computedValues);
    };
  }, []);

  return null;
}

function ControlValues({ controlValues, id, computeNode, computedValues }) {
  const value = controlValues[id];

  useEffect(() => {
    const [nodeId] = id.split(":");
    computeNode(nodeId, computedValues);
  }, [value]);

  return null;
}

export default ComputeContext;
